// src/api/programs.js
// ──────────────────────────────────────────────────────────────────────────
// Mbala Training Institute (MTI) — real TEVET skills courses across the four
// departments (Agriculture, Engineering, Hospitality, ICT). Pulls from
// /programs if your backend has it; otherwise serves this list.
// Images are themed placeholders — replace with MTI's own photos (the
// brochure has real photos of food, crops, poultry, welding, furniture).
// ──────────────────────────────────────────────────────────────────────────

import { fetchPrograms } from '../lib/cms';

// Departments (used as the "faculty" filter)
export const FACULTIES = ['Agriculture', 'Engineering', 'Hospitality', 'ICT'];
export const LEVELS = ['Trade Test', 'Certificate', 'Diploma', 'Short course'];

export const SAMPLE_PROGRAMS = [
  // ── Hospitality ──────────────────────────────────────────
  {
    id: 'food-production',
    title: 'Food Production & Cookery',
    faculty: 'Hospitality',
    level: 'Certificate',
    duration: '6–12 months',
    mode: 'Full-time · 80% practical',
    tuition: 'Enquire',
    summary: 'Professional cookery and food production — turn a daily need into a business and create employment.',
    image: 'https://images.pexels.com/photos/14621560/pexels-photo-14621560.jpeg?auto=compress&cs=tinysrgb&w=1600',
  },
  {
    id: 'catering',
    title: 'Professional & Outdoor Catering',
    faculty: 'Hospitality',
    level: 'Short course',
    duration: '3–6 months',
    mode: 'Full-time · hands-on',
    tuition: 'Enquire',
    summary: 'Cater weddings, kitchen parties, conferences and school snacks with confidence and flair.',
    image: 'https://images.pexels.com/photos/14621560/pexels-photo-14621560.jpeg?auto=compress&cs=tinysrgb&w=1600',
  },

  // ── Agriculture ──────────────────────────────────────────
  {
    id: 'general-agriculture',
    title: 'General Agriculture',
    faculty: 'Agriculture',
    level: 'Certificate',
    duration: '12 months',
    mode: 'Full-time · hands-on',
    tuition: 'Enquire',
    summary: 'Crop production, animal husbandry and farming as a business — not just as a tradition.',
    image: 'https://images.pexels.com/photos/34411687/pexels-photo-34411687.jpeg?auto=compress&cs=tinysrgb&w=1600',
  },
  {
    id: 'poultry',
    title: 'Poultry Production',
    faculty: 'Agriculture',
    level: 'Short course',
    duration: '3 months',
    mode: 'Full-time · hands-on',
    tuition: 'Enquire',
    summary: 'Raise broilers and layers for profit, with practical training in housing, feed and disease control.',
    image: 'https://images.pexels.com/photos/33670661/pexels-photo-33670661.jpeg?auto=compress&cs=tinysrgb&w=1600',
  },
  {
    id: 'crop-production',
    title: 'Crop & Vegetable Production',
    faculty: 'Agriculture',
    level: 'Short course',
    duration: '3–6 months',
    mode: 'Full-time · hands-on',
    tuition: 'Enquire',
    summary: 'Grow staple crops and vegetables using organic methods, geared towards selling for income.',
    image: 'https://images.pexels.com/photos/34705724/pexels-photo-34705724.jpeg?auto=compress&cs=tinysrgb&w=1600',
  },
  {
    id: 'fish-farming',
    title: 'Fish Farming (Aquaculture)',
    faculty: 'Agriculture',
    level: 'Short course',
    duration: '3–6 months',
    mode: 'Full-time · hands-on',
    tuition: 'Enquire',
    summary: 'Set up and run fish ponds — breams and other species — as a sustainable enterprise.',
    image: 'https://images.pexels.com/photos/33993456/pexels-photo-33993456.jpeg?auto=compress&cs=tinysrgb&w=1600',
  },

  // ── Engineering (Building & Construction) ────────────────
  {
    id: 'building-plastering',
    title: 'Building & Plastering',
    faculty: 'Engineering',
    level: 'Certificate',
    duration: '6–12 months',
    mode: 'Full-time · 80% practical',
    tuition: 'Enquire',
    summary: 'Bricklaying, plastering and general construction skills for employment and contracts.',
    image: 'https://images.pexels.com/photos/29006128/pexels-photo-29006128.jpeg?auto=compress&cs=tinysrgb&w=1600',
  },
  {
    id: 'carpentry',
    title: 'Carpentry & Joinery',
    faculty: 'Engineering',
    level: 'Certificate',
    duration: '6–12 months',
    mode: 'Full-time · 80% practical',
    tuition: 'Enquire',
    summary: 'Produce desks, furniture and joinery — supply schools, offices and homes.',
    image: 'https://images.pexels.com/photos/3760930/pexels-photo-3760930.jpeg?auto=compress&cs=tinysrgb&w=1600',
  },
  {
    id: 'metal-fabrication',
    title: 'Metal Fabrication & Welding',
    faculty: 'Engineering',
    level: 'Certificate',
    duration: '6–12 months',
    mode: 'Full-time · 80% practical',
    tuition: 'Enquire',
    summary: 'Welding, door and window frames, gates and general sheet-metal fabrication.',
    image: 'https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?auto=format&fit=crop&q=80&w=1600',
  },
  {
    id: 'plumbing',
    title: 'Plumbing & Sheet Metal',
    faculty: 'Engineering',
    level: 'Certificate',
    duration: '6 months',
    mode: 'Full-time · hands-on',
    tuition: 'Enquire',
    summary: 'Install and maintain plumbing systems and carry out practical sheet-metal work.',
    image: 'https://images.unsplash.com/photo-1607472586893-edb57bdc0e39?auto=format&fit=crop&q=80&w=1600',
  },
  {
    id: 'electrical',
    title: 'Electrical Engineering',
    faculty: 'Engineering',
    level: 'Certificate',
    duration: '6–12 months',
    mode: 'Full-time · hands-on',
    tuition: 'Enquire',
    summary: 'Domestic and industrial electrical installation and maintenance.',
    image: 'https://images.unsplash.com/photo-1621905251189-08b45d6a269e?auto=format&fit=crop&q=80&w=1600',
  },
  {
    id: 'tailoring',
    title: 'Cutting, Designing & Tailoring',
    faculty: 'Engineering',
    level: 'Short course',
    duration: '3–6 months',
    mode: 'Full-time · hands-on',
    tuition: 'Enquire',
    summary: 'Make uniforms and garments and supply schools and private businesses locally.',
    image: 'https://images.unsplash.com/photo-1556905055-8f358a7a47b2?auto=format&fit=crop&q=80&w=1600',
  },

  // ── ICT ──────────────────────────────────────────────────
  {
    id: 'ict',
    title: 'ICT & Computer Skills',
    faculty: 'ICT',
    level: 'Short course',
    duration: '3–6 months',
    mode: 'Full-time · hands-on',
    tuition: 'Enquire',
    summary: 'Computer literacy and ICT skills, integrated across every trade for modern business.',
    image: 'https://images.unsplash.com/photo-1517694712202-14dd9538aa97?auto=format&fit=crop&q=80&w=1600',
  },
];

const mapRow = (r) => ({ ...r, id: r.slug });

const ProgramsAPI = {
  getAll: async () => {
    const rows = await fetchPrograms();
    if (rows && rows.length) return { success: true, programs: rows.map(mapRow) };
    return { success: true, programs: SAMPLE_PROGRAMS };
  },
  getOne: async (id) => {
    const rows = await fetchPrograms();
    if (rows && rows.length) {
      const r = rows.find((x) => x.slug === id);
      if (r) return mapRow(r);
    }
    return SAMPLE_PROGRAMS.find((p) => p.id === id) || null;
  },
};

export default ProgramsAPI;
